import React, { useEffect, useRef } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setHasImportantUnsavedChanges } from '../store/features/unsavedChangesSlice';

// Reads hasImportantUnsavedChanges from redux directly, so it can be mounted once (e.g. in
// AdminRoute/ConsultantRoute) instead of being wired up with a prop in every form that tracks
// unsaved changes.
const UnsavedChangesPromptBeforeUnload = () => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const hasImportantUnsavedChanges = useSelector(state => state.unsavedChanges.hasImportantUnsavedChanges);
  const message = t(window.MARKET_CONSTANTS?.UNSAVED_CHANGES_MESSAGE);
  const hasImportantUnsavedChangesRef = useRef(hasImportantUnsavedChanges);

  // Keep ref in sync with state
  useEffect(() => {
    hasImportantUnsavedChangesRef.current = hasImportantUnsavedChanges;
  }, [hasImportantUnsavedChanges]);

  // Add event listener on mount, remove on unmount
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (hasImportantUnsavedChangesRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Reset once the router's location actually changes — that only happens once the Prompt above
  // has been confirmed (or there was nothing to confirm), so any changes it was tracking are now
  // gone. This has to key off the location changing rather than this component unmounting:
  // navigating between sibling sub-pages that share one route with a dynamic param (e.g.
  // creating_application/:page) never unmounts the route wrapper this is nested in, even though
  // the URL — and the page's content — genuinely changed.
  useEffect(() => {
    const unlisten = history.listen(() => {
      if (hasImportantUnsavedChangesRef.current) {
        dispatch(setHasImportantUnsavedChanges(false));
      }
    });
    return unlisten;
  }, [history, dispatch]);
  return /*#__PURE__*/React.createElement(Prompt, {
    when: hasImportantUnsavedChanges,
    message: message
  });
};
export default UnsavedChangesPromptBeforeUnload;